/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Button,
  Flex,
  SegmentedControl,
  Box
} from "@mantine/core";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  queryClient, useAllAutomations, useAllPlaybooks, useCurrentUser, useWorkspaceObjectSchema,
} from "../../../../hooks/QueryHooks";
import { createAutomation } from "../../../../services/automation/automationEndpoints";
import useSignalStore from "../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import useHeaderStore from "../../../../store/layout/headerStore";
import notify from "../../../../utils/notify";
import NewSignalBody from "./components/NewSignalBody";
import { getDefaultSortField } from "../../../../shared/filter/utils/entityDisplayUtils";
import RecommendedSignals from "./components/RecommendedSignals";

const NewSignalPage = () => {
  const {
    signal,
    setSignal,
    setName,
    setAutomationType,
    setEntityRelation,
    setTrigger,
    setMetrics,
    setSelectedFields,
    addFilter,
    addPredicate,
    editPredicate,
    deletePredicate,
    deleteFilter,
    clearFilter,
    clearSignal,
    setStatus,
    setTimeWindow,
    setTimeZone,
    setSuppressionPeriod,
    setModelKey,
    createAction,
    deleteAction,
    setPlaybook,
    updateAction,
    setResponseData,
    setSort,
  } = useSignalStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { setHeaderContent, clearHeader, resetHeader } = useHeaderStore();
  const [error, setError] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>("custom");

  const workspaceSchema: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: user }: any = useCurrentUser({});

  const { data: playBookData }: any = useAllPlaybooks({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  const playbooks = useMemo(() => playBookData?.map((playbook: any) => ({
    label: playbook?.title,
    value: playbook?.id,
  })), [playBookData?.length]);

  const createSignalMutation = useMutation({
    mutationFn: createAutomation,
  });

  const navigate = useNavigate();

  const onTimeWindowChange = (value: any) => {
    setTimeWindow(value);
  };

  const onSignalSubmit = () => {
    // Get the latest signal value from the store to avoid stale closure issues
    const currentSignal = useSignalStore.getState().signal;

    const slackEnabled = currentSignal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_slack");
    const emailEnabled = currentSignal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_email");
    if (!currentSignal?.name || !currentSignal?.entity_relation || !currentSignal?.trigger
      || !currentSignal?.filters?.predicates[0]?.predicates?.length
      || !currentSignal?.automation_actions?.actions?.length
      || !currentSignal?.automation_actions?.suppression_period
      || !currentSignal?.time_window
      || (!!slackEnabled && ((slackEnabled?.message_type === "channel" && !slackEnabled?.channel_id)
        || (slackEnabled?.message_type === "direct" && !slackEnabled?.slack_user_id)))
      || (!!emailEnabled && (!emailEnabled?.to || !emailEnabled?.subject))) {
      setError(true);
      notify({
        messageList: ["Kindly resolve errors in form fields"],
        type: "warning",
      });
    } else {
      // Fix sort before submission
      let sortConfig = { ...currentSignal.sort };

      // If sort field is empty or invalid, set default based on entity type
      if (!sortConfig.field || sortConfig.field === "") {
        sortConfig = {
          field: getDefaultSortField(currentSignal.entity_relation || "accounts"),
          order: "desc",
          type: "property",
        };
      }

      // Ensure sort type is always defined
      if (!sortConfig.type) {
        sortConfig.type = "property";
      }

      // Ensure selected_fields is not empty - use empty array as default
      const selectedFields = currentSignal.selected_fields && currentSignal.selected_fields.length > 0
        ? currentSignal.selected_fields
        : [];

      createSignalMutation.mutate({
        ...currentSignal,
        selected_fields: selectedFields,
        metrics: [],
        sort: sortConfig as any,
        time_zone: workspace?.workspace_time_zone,
        status: "active",
        workspace_id: workspace?.id,
        owner_id: user?.user_id,
      }, {
        onSuccess: (data: any) => {
          queryClient.setQueryData(["all_automations", workspace?.id, currentSignal?.automation_type], (oldData: any) => ({
            ...oldData,
            data: [
              ...oldData.data,
              data?.data,
            ],
          }));
          if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
            window?.hyperengage(
              "track",
              "new_signal_created",
              {
                properties: {
                  name: currentSignal?.name,
                  entity_relation: currentSignal?.entity_relation,
                  trigger: currentSignal?.trigger,
                  actions: currentSignal?.automation_actions?.actions?.map((action: any) => action?.type),
                  time_zone: workspace?.workspace_time_zone,
                  status: "active",
                  workspace_id: workspace?.id,
                  owner_id: user?.user_id,
                },
              },
            );
          }
          notify({
            heading: "New Signal Created",
            type: "success",
            messageList: ["You have successfully created a new Signal."],
          });
          clearSignal();
          navigate(-1);
        },
        onError: (err: any) => {
          if (err.response?.status !== 0) {
            notify({
              heading: "Unknown error.",
              type: "failure",
              messageList: [
                "An unknown error occurred while creating the signal. Please try again later or contact our support team for assistance.",
              ],
            });
          }
        },
      });
    }
  };

  // Header Actions Integration
  useEffect(() => {
    setHeaderContent({
      title: "Create New Signal",
      description: "Configure triggers and actions for your signal",
      actions: (
        <Flex gap="md" align="center">
          <SegmentedControl
            value={activeTab}
            onChange={setActiveTab}
            data={[
              { label: "Custom Signal", value: "custom" },
              { label: "Recommended Templates", value: "recommended" },
            ]}
            size="sm"
            radius="md"
          />

          {activeTab === "custom" && (
            <Flex gap="xs">
              <Button
                variant="default"
                size="xs"
                onClick={() => {
                  clearSignal();
                  navigate(-1);
                }}
                disabled={createSignalMutation.isLoading}
                leftSection={<IconX size={14} />}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                onClick={onSignalSubmit}
                loading={createSignalMutation.isLoading}
                leftSection={<IconDeviceFloppy size={14} />}
              >
                {createSignalMutation.isLoading ? 'Saving...' : 'Save Signal'}
              </Button>
            </Flex>
          )}
        </Flex>
      )
    });

    return () => {
      resetHeader();
    };
  }, [activeTab, createSignalMutation.isLoading]);

  const handleFilterAdd = () => {
    const selectedFilters = signal
      .filters.predicates[signal.filters.predicates.length - 1]?.predicates || [];
    if (selectedFilters.length === 0 || selectedFilters[0].field !== "") {
      addFilter({
        query_type: "AND",
        predicates: [{
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        }],
      });
    }
  };

  const handlePredicateAdd = (filterId?: any) => {
    const selectedFilters = signal.filters.predicates[filterId].predicates;
    if (selectedFilters.length === 0 || selectedFilters[selectedFilters.length - 1].field !== "") {
      addPredicate(
        {
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        },
        filterId,
      );
    }
  };

  const handlePredicateClear = (filterId?: any) => {
    deleteFilter(filterId);
  };

  const handleEntityChange = (value: string) => {
    // Only proceed if entity relation is actually changing
    if (signal.entity_relation === value) {
      return;
    }

    // Clear filters first to avoid state conflicts
    clearFilter();

    // Set entity relation
      setEntityRelation(value);

      // Set defaults based on new entity type
      if (value === "accounts") {
        setSelectedFields(["accounts.name", "accounts.last_seen", "accounts.created_at"]);
        setSort({
          field: "accounts.last_seen",
          order: "desc",
          type: "property",
        });
      } else if (value === "users") {
        setSelectedFields(["users.name", "users.first_name", "users.last_seen", "users.created_at"]);
        setSort({
          field: "users.last_seen",
          order: "desc",
          type: "property",
        });
      }
  };

  const handlePredicateChange = (
    field: string,
    operator: string,
    value: any,
    type: string,
    entity_type?: string,
    id?: any,
    filterId?: any,
    time_window?: string,
  ) => {
    editPredicate({
      field,
      operator,
      value,
      type,
      entity_type: entity_type || "column",
      time_window,
    }, id, filterId);
    const selectedColumns = signal?.selected_fields || [];
    let newColumns: any = selectedColumns;
    if (signal?.entity_relation === "accounts" && field.startsWith("accounts.")) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    } else if (signal?.entity_relation === "users" && field.startsWith("users.")) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    }
  };

  const handlePredicateRemove = (id: number, filterId?: any) => {
    deletePredicate(id, filterId);
  };

  useEffect(
    () => () => { clearSignal(); },
    [],
  );

  return (
    <Box className="h-[calc(100vh-100px)] flex flex-col bg-gray-50 overflow-hidden">
      {/* Content Section */}
      <Box className="flex-1 overflow-hidden flex flex-col relative">
        {activeTab === "custom" ? (
          <div className="flex-1 overflow-auto min-h-full">
            <NewSignalBody
              clearFilter={clearFilter}
              clearSignal={clearSignal}
              onTimeWindowChange={onTimeWindowChange}
              handlePredicateAdd={handlePredicateAdd}
              handlePredicateClear={handlePredicateClear}
              handlePredicateRemove={handlePredicateRemove}
              handlePredicateChange={handlePredicateChange}
              handleFilterAdd={handleFilterAdd}
              onSignalSubmit={onSignalSubmit}
              error={error}
              total={total}
              workspaceSchema={workspaceSchema}
              setSelectedMetrics={setMetrics}
              signal={signal}
              setName={setName}
              setEntityRelation={handleEntityChange}
              setTrigger={setTrigger}
              setSuppressionPeriod={setSuppressionPeriod}
              setModelKey={setModelKey}
              setSelectedFields={setSelectedFields}
              createAction={createAction}
              deleteAction={deleteAction}
              updateAction={updateAction}
              playbooks={playbooks}
              setSignal={setSignal}
              setPlaybook={setPlaybook}
            />
          </div>
        ) : (
          <Box className="flex-1 overflow-hidden">
            <div className="mx-auto h-full">
              <RecommendedSignals onSignalSelected={() => setActiveTab("custom")} />
            </div>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NewSignalPage;
